import { Vendor } from '../../../webrtc/media';
import {
  CommonRealtimeService,
  type AckResult,
} from './commonRealtime.service';

import {
  type CallStartMessagePayload,
  RealtimeMessageType,
} from '../../../features/realtime/realtime.types';

type InviteMemberOptions = {
  dbSessionId: string;
  sessionId: string;
  isAudioCall: boolean;
  startCallTime: number;
  joinedParticipantsIds?: string[];
};

export class ProviderRealtimeService extends CommonRealtimeService {
  /**
   * Send a message to a patient to start a call session
   */
  inviteMembers(
    joinTokens: { identityId: string; token: string }[],
    {
      sessionId,
      dbSessionId,
      isAudioCall,
      startCallTime,
      joinedParticipantsIds,
    }: InviteMemberOptions,
  ): void {
    for (const { identityId, token } of joinTokens) {
      const payload: CallStartMessagePayload = {
        sessionId,
        dbSessionId,
        vendor: Vendor.Vonage,
        isAudioCall,
        startCallTime,
        joinedParticipantsIds,
        joinToken: token,
      };

      void this.send(identityId, {
        messageType: RealtimeMessageType.CallStart,
        payload,
      });
    }
  }

  /**
   * Pause the current call by sending a hold message to the patient
   * @param array participant ids
   */
  async putMembersOnHold(
    participants: string[],
    sessionOwnershipToken: string,
  ): Promise<AckResult[]> {
    const promises: Promise<AckResult>[] = [];
    for (const participantId of participants) {
      promises.push(
        this.sendWithAck(participantId, {
          messageType: RealtimeMessageType.CallHold,
          payload: { sessionOwnershipToken },
        }),
      );
    }
    return Promise.all(promises);
  }

  async resumeMembersOnHold(
    joinTokens: { identityId: string; token: string }[],
    { sessionId, isAudioCall, startCallTime }: InviteMemberOptions,
  ): Promise<AckResult> {
    const promises = [];
    for (const { identityId, token } of joinTokens) {
      const payload: CallStartMessagePayload = {
        sessionId,
        vendor: Vendor.Vonage,
        isAudioCall,
        startCallTime,
        joinToken: token,
      };

      promises.push(
        this.sendWithAck(identityId, {
          messageType: RealtimeMessageType.CallResume,
          payload,
        }),
      );
    }
    // In order to support group calls effectively we return a promise which resolves
    // when a single participant to acknowledges the call resume realtime message.
    return Promise.any(promises);
  }
}
